import React from 'react'
import SellerShippingOptions from '@/components/Shipping/SellerShippingOptions'
import ShippingPriceLabel from '@/components/Shipping/ShippingPriceLabel'
import type { ISelectShippingRequest, ISellerShippingQuote, IShippingQuote } from '~/schemas/shared/shipping'

interface IShippingQuotesProps {
    quotes: IShippingQuote[]
    /** Frete cobrado no carrinho (`cart.shippingTax`): o da primeira cotação, já com o efeito do cupom. */
    shippingTax: number
    /** O carrinho tem CEP: mostra o "Opções de frete para". */
    hasZipCode: boolean
    /** A cotação dos itens de agora falhou: com CEP, avisa em vez de pedir o CEP que já está preenchido. */
    quoteFailed?: boolean
    /** Frete por vendedor (staging/api): quando há, substitui a lista de cotações. */
    sellers?: ISellerShippingQuote[]
    /** Escolha de frete em voo, ou `null`. */
    pendingSelection?: ISelectShippingRequest | null
    onSelectShipping?: (sellerId: string, offerCode: string) => void
}

const NO_SELLERS: ISellerShippingQuote[] = []
const noop = () => {}

/**
 * Cotações do frete do carrinho. Com frete por vendedor (staging/api), as opções de cada vendedor, que o cliente
 * escolhe. Sem ele (production/api), a API cobra sempre a primeira cotação (marcada) e as outras são informativas, cada
 * uma com o próprio preço e prazo (o legado repetia o frete cobrado em todas). Frete zero aparece como "Grátis". Sem
 * cotação, pede o CEP.
 */
const ShippingQuotes: React.FC<IShippingQuotesProps> = ({
    quotes,
    shippingTax,
    hasZipCode,
    quoteFailed = false,
    sellers = NO_SELLERS,
    pendingSelection = null,
    onSelectShipping = noop,
}) => {
    if (quotes.length === 0 && sellers.length === 0) {
        if (quoteFailed && hasZipCode) {
            return (
                <p className="p-2 text-lg font-extrabold uppercase text-destaques-hover font-sora max-md:hidden">
                    Frete não calculado para os itens
                </p>
            )
        }
        return (
            <p className="p-2 text-lg font-extrabold uppercase text-destaques-hover font-sora max-md:hidden">
                Informe o Cep
            </p>
        )
    }

    return (
        <div className="p-2 rounded-lg bg-lightgray">
            {hasZipCode && quotes[0]?.location && (
                <p className="py-2 px-3 font-normal border-b text-destaques-hover border-submenu-gray bg-opacity:0.5 ">
                    Opções de frete para <span className="font-bold text-underline">{quotes[0].location}</span>
                </p>
            )}
            {sellers.length > 0 ? (
                <SellerShippingOptions sellers={sellers} pending={pendingSelection} onSelect={onSelectShipping} />
            ) : (
                quotes.map((quote, index) => (
                    <label
                        key={`${quote.service}-${quote.name}`}
                        htmlFor={`freight-${index}`}
                        className="flex flex-col py-2 my-2 rounded-lg cursor-pointer hover:bg-submenu-gray"
                    >
                        <div className="flex flex-row items-center gap-2 ml-2">
                            <input
                                type="radio"
                                name="freight"
                                id={`freight-${index}`}
                                value={quote.service}
                                checked={index === 0}
                                readOnly
                                className="w-4 h-4"
                            />
                            <span className="w-full cursor-pointer max-md:text-sm text-md">
                                {quote.name} - <ShippingPriceLabel price={index === 0 ? shippingTax : quote.price} /> -{' '}
                                {quote.days} dias úteis
                            </span>
                        </div>
                    </label>
                ))
            )}
        </div>
    )
}

export default ShippingQuotes
